// src/components/settings/WhiteboardSettings.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Palette, Video, Eye, EyeOff, Type, Bold, Italic, Underline, Minus, Plus, X } from 'lucide-react';

const SettingsContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 350px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 2000;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  overflow-y: auto;
`;

const SettingsHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SettingsTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const SettingsSection = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const ColorOption = styled.button`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  background: ${props => props.color};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ControlLabel = styled.label`
  font-size: 14px;
  color: #374151;
  min-width: 120px;
`;

const ToggleSwitch = styled.button`
  width: 50px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? '#3b82f6' : '#d1d5db'};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

const Slider = styled.input`
  width: 100px;
  margin: 0 8px;
`;

const SliderValue = styled.span`
  font-size: 14px;
  color: #6b7280;
  min-width: 30px;
  text-align: center;
`;

const TextStyleGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const StyleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f9fafb'};
  }
`;

const UserList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
  background: white;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.color || '#3b82f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 600;
`;

const UserName = styled.span`
  font-size: 14px;
  color: #374151;
`;

const WhiteboardSettings = ({ isOpen, onClose, onSettingsChange }) => {
  const [settings, setSettings] = useState({
    // Blackboard background colors
    backgroundColors: [
      '#000000', '#1a1a1a', '#2d2d2d', '#404040',
      '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6',
      '#0f172a', '#1e293b', '#334155', '#475569',
      '#1f2937', '#374151', '#4b5563', '#6b7280'
    ],
    selectedBackground: '#000000',
    
    // Text settings
    textSize: 16,
    textColor: '#ffffff',
    textStyle: {
      bold: false,
      italic: false,
      underline: false
    },
    
    // Video settings
    videoEnabled: true,
    userVideoSettings: {},
    
    // Panel visibility
    panelsVisible: {
      left: true,
      right: true,
      bottom: true,
      top: true
    }
  });

  const [users, setUsers] = useState([
    { id: '1', name: 'Teacher', avatar: 'T', color: '#3b82f6', videoEnabled: true },
    { id: '2', name: 'Student 1', avatar: 'S1', color: '#10b981', videoEnabled: true },
    { id: '3', name: 'Student 2', avatar: 'S2', color: '#f59e0b', videoEnabled: false },
    { id: '4', name: 'Student 3', avatar: 'S3', color: '#ef4444', videoEnabled: true }
  ]);

  useEffect(() => {
    if (onSettingsChange) {
      onSettingsChange(settings);
    }
  }, [settings, onSettingsChange]);

  const handleBackgroundChange = (color) => {
    setSettings(prev => ({
      ...prev,
      selectedBackground: color
    }));
  };

  const handleTextSizeChange = (size) => {
    setSettings(prev => ({
      ...prev,
      textSize: parseInt(size)
    }));
  };

  const handleTextColorChange = (color) => {
    setSettings(prev => ({
      ...prev,
      textColor: color
    }));
  };

  const handleTextStyleChange = (style) => {
    setSettings(prev => ({
      ...prev,
      textStyle: {
        ...prev.textStyle,
        [style]: !prev.textStyle[style]
      }
    }));
  };

  const handleUserVideoToggle = (userId) => {
    setUsers(prev => prev.map(user => 
      user.id === userId 
        ? { ...user, videoEnabled: !user.videoEnabled }
        : user
    ));
  };

  const handlePanelVisibilityToggle = (panel) => {
    setSettings(prev => ({
      ...prev,
      panelsVisible: {
        ...prev.panelsVisible,
        [panel]: !prev.panelsVisible[panel]
      }
    }));
  };

  return (
    <SettingsContainer isOpen={isOpen}>
      <SettingsHeader>
        <SettingsTitle>
          <Palette size={20} />
          白板设置 (Whiteboard Settings)
        </SettingsTitle>
        <CloseButton onClick={onClose}>
          <X size={16} />
        </CloseButton>
      </SettingsHeader>

      {/* Background Color Control */}
      <SettingsSection>
        <SectionTitle>
          <Palette size={16} />
          背景颜色 (Background Color)
        </SectionTitle>
        <ColorGrid>
          {settings.backgroundColors.map((color, index) => (
            <ColorOption
              key={index}
              color={color}
              selected={settings.selectedBackground === color}
              onClick={() => handleBackgroundChange(color)}
            />
          ))}
        </ColorGrid>
      </SettingsSection>

      {/* Text Controls */}
      <SettingsSection>
        <SectionTitle>
          <Type size={16} />
          文字设置 (Text Settings)
        </SectionTitle>
        
        <ControlGroup>
          <ControlLabel>字体大小:</ControlLabel>
          <Minus size={16} />
          <Slider
            type="range"
            min="8"
            max="72"
            value={settings.textSize}
            onChange={(e) => handleTextSizeChange(e.target.value)}
          />
          <Plus size={16} />
          <SliderValue>{settings.textSize}px</SliderValue>
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>文字颜色:</ControlLabel>
          <ColorGrid style={{ gridTemplateColumns: 'repeat(8, 1fr)', width: '200px' }}>
            {['#ffffff', '#000000', '#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#6b7280'].map((color, index) => (
              <ColorOption
                key={index}
                color={color}
                selected={settings.textColor === color}
                onClick={() => handleTextColorChange(color)}
                style={{ width: '24px', height: '24px' }}
              />
            ))}
          </ColorGrid>
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>文字样式:</ControlLabel>
          <TextStyleGroup>
            <StyleButton
              active={settings.textStyle.bold}
              onClick={() => handleTextStyleChange('bold')}
            >
              <Bold size={16} />
            </StyleButton>
            <StyleButton
              active={settings.textStyle.italic}
              onClick={() => handleTextStyleChange('italic')}
            >
              <Italic size={16} />
            </StyleButton>
            <StyleButton
              active={settings.textStyle.underline}
              onClick={() => handleTextStyleChange('underline')}
            >
              <Underline size={16} />
            </StyleButton>
          </TextStyleGroup>
        </ControlGroup>
      </SettingsSection>

      {/* Video Controls */}
      <SettingsSection>
        <SectionTitle>
          <Video size={16} />
          视频控制 (Video Control)
        </SectionTitle>
        
        <ControlGroup>
          <ControlLabel>视频总开关:</ControlLabel>
          <ToggleSwitch
            active={settings.videoEnabled}
            onClick={() => setSettings(prev => ({ ...prev, videoEnabled: !prev.videoEnabled }))}
          />
        </ControlGroup>

        <UserList>
          {users.map(user => (
            <UserItem key={user.id}>
              <UserInfo>
                <UserAvatar color={user.color}>{user.avatar}</UserAvatar>
                <UserName>{user.name}</UserName>
              </UserInfo>
              <ToggleSwitch
                active={user.videoEnabled}
                onClick={() => handleUserVideoToggle(user.id)}
              />
            </UserItem>
          ))}
        </UserList>
      </SettingsSection>

      {/* Panel Visibility Controls */}
      <SettingsSection>
        <SectionTitle>
          <Eye size={16} />
          面板显示 (Panel Visibility)
        </SectionTitle>
        
        <ControlGroup>
          <ControlLabel>左侧面板:</ControlLabel>
          <ToggleSwitch
            active={settings.panelsVisible.left}
            onClick={() => handlePanelVisibilityToggle('left')}
          />
        </ControlGroup>
        
        <ControlGroup>
          <ControlLabel>右侧面板:</ControlLabel>
          <ToggleSwitch
            active={settings.panelsVisible.right}
            onClick={() => handlePanelVisibilityToggle('right')}
          />
        </ControlGroup>
        
        <ControlGroup>
          <ControlLabel>底部面板:</ControlLabel>
          <ToggleSwitch
            active={settings.panelsVisible.bottom}
            onClick={() => handlePanelVisibilityToggle('bottom')}
          />
        </ControlGroup>
        
        <ControlGroup>
          <ControlLabel>顶部面板:</ControlLabel>
          <ToggleSwitch
            active={settings.panelsVisible.top}
            onClick={() => handlePanelVisibilityToggle('top')}
          />
        </ControlGroup>
      </SettingsSection>
    </SettingsContainer>
  );
};

export default WhiteboardSettings;
